import { useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { Spinner } from '@/components/ui/spinner';
import {
  Icon3dCubeSphere,
  IconBox,
  IconBraces,
  IconBrush,
  IconFile,
  IconFileText,
  IconMovie,
  IconMusic,
  IconPhoto,
  IconScript,
  IconTexture,
} from '@tabler/icons-react';
import { api, type DamJobState } from '../api.js';

interface ThumbnailCardProps {
  depotPath: string;
  headRev: number;
  filename: string;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'rendering'; jobId: number }
  | { kind: 'ready'; url: string }
  | { kind: 'fallback' };

const POLL_MS = 1500;

/**
 * Tiny card used in the directory grid view. Defers the thumbnail
 * request until the card scrolls into view, so opening a 1000-file
 * directory doesn't fan out 1000 simultaneous render requests.
 */
export function ThumbnailCard({ depotPath, headRev, filename }: ThumbnailCardProps) {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Latest blob URL we created. Held in a ref so the fetch effect's
  // re-run (triggered by setState({kind:'ready'})) doesn't revoke a URL
  // the rendered <img> still depends on. We revoke once on unmount.
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Intersection-observer trigger: flip to 'loading' the first time
  // this card scrolls into the viewport.
  useEffect(() => {
    if (state.kind !== 'idle') return;
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setState({ kind: 'loading' });
          io.disconnect();
          return;
        }
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [state.kind]);

  // Once loading, fetch the thumbnail; if it's not cached yet we'll
  // poll the job endpoint at a slower cadence than the file view.
  useEffect(() => {
    if (state.kind !== 'loading' && state.kind !== 'rendering') return;
    let cancelled = false;

    async function fetchThumb(): Promise<void> {
      const url = api.damThumbnailUrl(depotPath, headRev, 256);
      const res = await fetch(url, { credentials: 'include' });
      if (cancelled) return;
      const contentType = res.headers.get('content-type') ?? '';
      if (res.status === 200 && contentType.startsWith('image/')) {
        const blob = await res.blob();
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const newUrl = URL.createObjectURL(blob);
        blobUrlRef.current = newUrl;
        setState({ kind: 'ready', url: newUrl });
        return;
      }
      if (res.status === 200 && contentType.includes('json')) {
        // {noPreview: true} sentinel — render the typed fallback icon.
        setState({ kind: 'fallback' });
        return;
      }
      if (res.status === 202) {
        const body = (await res.json()) as { jobId: number; state: DamJobState };
        if (cancelled) return;
        setState({ kind: 'rendering', jobId: body.jobId });
        scheduleNext(body.jobId);
        return;
      }
      setState({ kind: 'fallback' });
    }

    async function scheduleNext(jobId: number): Promise<void> {
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (cancelled) return;
      try {
        const job = await api.damJob(jobId);
        if (cancelled) return;
        if (job.state === 'done') {
          await fetchThumb();
          return;
        }
        if (job.state === 'failed') {
          // Don't re-enter fetchThumb on failure: the backend will
          // accept the request, see no cached artifact, return 202
          // with a fresh jobId, mark the new job failed, and we'd
          // poll our way into the same dead end forever. Render the
          // typed fallback icon instead.
          setState({ kind: 'fallback' });
          return;
        }
        scheduleNext(jobId);
      } catch {
        if (!cancelled) setState({ kind: 'fallback' });
      }
    }

    fetchThumb().catch(() => {
      if (!cancelled) setState({ kind: 'fallback' });
    });

    return () => {
      cancelled = true;
    };
  }, [state.kind, depotPath, headRev]);

  return (
    <div
      ref={rootRef}
      style={{
        width: '100%',
        aspectRatio: '1 / 1',
        background:
          'repeating-conic-gradient(#1a1f29 0% 25%, #0d1117 0% 50%) 50% / 12px 12px',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {state.kind === 'ready' ? (
        <img
          src={state.url}
          alt={filename}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : state.kind === 'fallback' ? (
        <FallbackIcon filename={filename} />
      ) : state.kind === 'loading' || state.kind === 'rendering' ? (
        <Spinner size="sm" />
      ) : (
        <FallbackIcon filename={filename} muted />
      )}
    </div>
  );
}

type TablerIcon = ComponentType<{
  size?: number;
  stroke?: number;
  style?: React.CSSProperties;
}>;

function FallbackIcon({ filename, muted = false }: { filename: string; muted?: boolean }) {
  const Icon = iconForFilename(filename);
  return (
    <Icon
      size={36}
      stroke={1.5}
      style={{
        color: '#7d8590',
        opacity: muted ? 0.25 : 0.55,
      }}
    />
  );
}

// Filename-driven icon picker. Mirrors the Epic asset-class prefixes used
// by the renderer's inferAssetClass() so the user gets a consistent
// "looks like an X" signal whether the embedded thumbnail rendered or not.
function iconForFilename(name: string): TablerIcon {
  const lower = name.toLowerCase();
  const ext = lower.includes('.') ? lower.slice(lower.lastIndexOf('.') + 1) : '';

  if (ext === 'uasset') {
    if (/^t_|^tc_|^tr_/.test(lower)) return IconTexture;
    if (/^sm_|^sk_/.test(lower)) return IconBox;
    if (/^m_|^mi_|^mf_/.test(lower)) return IconBrush;
    if (/^bp_|^abp_|^wbp_/.test(lower)) return IconScript;
    if (/^ns_|^ne_|^p_/.test(lower)) return Icon3dCubeSphere;
    if (/^s_|^sc_|^sw_/.test(lower)) return IconMusic;
    return IconBox;
  }
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'tga', 'bmp', 'tiff', 'tif', 'psd', 'exr', 'hdr'].includes(ext)) return IconPhoto;
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) return IconMovie;
  if (['mp3', 'wav', 'ogg', 'flac', 'aiff'].includes(ext)) return IconMusic;
  if (['json', 'uefnproject', 'uproject', 'uplugin'].includes(ext)) return IconBraces;
  if (['txt', 'md', 'markdown', 'verse'].includes(ext)) return IconFileText;
  return IconFile;
}
